/**
 * Tooltip — portal-rendered hover hint, measured against the live viewport.
 *
 * [REQ-SEP-04 Band 0.5] REWRITTEN from the CSS-only version (premise row 13). The old bubble
 * was an absolutely-positioned CHILD of the trigger wrapper, which put it inside every shell
 * aside's `overflow: hidden` — so a CSS override stack grew in index.css to fight it: an
 * `aside nav`-scoped position:fixed escape that missed the toggle and Logout tooltips (both
 * live OUTSIDE <nav>), a hard `left: 78px` literal that assumed the collapsed rail at x=0,
 * and a belt-and-braces `overflow: visible` immediately re-clipped by its own longhands.
 * That stack is DELETED with this rewrite: the bubble portals to <body> (the BDAY-RING-FIX
 * precedent — no ancestor can clip, capture, or transform it away), positions itself from the
 * trigger's measured rect, and flips ONCE at a viewport edge after measuring its own box.
 *
 * Preserved contract, so all 8 consumers (6 shells + LanguageIconButton) change nothing:
 * the {text, children, position} API · the 300ms show delay · hidden under 768px (now a JS
 * gate — CSS :hover rules cannot reach a portalled bubble, which is also why show/hide moved
 * to mouse/focus handlers) · the exact bubble visual style · keyboard focus shows it too.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  text: string;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

type Pos = 'top' | 'bottom' | 'left' | 'right';
const GAP = 6;
const FLIP: Record<Pos, Pos> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Fixed-position anchor for the bubble, derived from the trigger's rect — never a literal. */
function anchorFor(r: DOMRect, pos: Pos): { top: number; left: number; transform: string } {
  switch (pos) {
    case 'top': return { top: r.top - GAP, left: r.left + r.width / 2, transform: 'translate(-50%, -100%)' };
    case 'bottom': return { top: r.bottom + GAP, left: r.left + r.width / 2, transform: 'translateX(-50%)' };
    case 'left': return { top: r.top + r.height / 2, left: r.left - GAP, transform: 'translate(-100%, -50%)' };
    case 'right': return { top: r.top + r.height / 2, left: r.right + GAP, transform: 'translateY(-50%)' };
  }
}

export function Tooltip({ text, children, position = 'bottom' }: TooltipProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const [box, setBox] = useState<{ top: number; left: number; transform: string; flipped: boolean } | null>(null);

  const show = (): void => {
    if (window.innerWidth < 768) return; // mobile: no hover, no tooltip — the old CSS rule, now here
    if (timer.current !== null) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setBox({ ...anchorFor(r, position), flipped: false });
    }, 300);
  };
  const hide = (): void => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    setBox(null);
  };
  // Unmount safety: never leak the delay timer or strand a portalled bubble on <body>.
  useEffect(() => hide, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * One measured flip, then stop. The bubble renders at its preferred side first; if its OWN
   * measured box overflows the viewport, the side is mirrored exactly once (`flipped` guards
   * against oscillation on a bubble too big for either side — it then stays on the mirror).
   */
  useLayoutEffect(() => {
    if (!box || box.flipped || !bubbleRef.current) return;
    const b = bubbleRef.current.getBoundingClientRect();
    if (b.left < 4 || b.top < 4 || b.right > window.innerWidth - 4 || b.bottom > window.innerHeight - 4) {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setBox({ ...anchorFor(r, FLIP[position]), flipped: true });
    }
  }, [box, position]);

  return (
    <div
      ref={wrapRef}
      className="ais-tooltip-wrap"
      style={{ display: 'inline-flex' }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {box && createPortal(
        <div
          ref={bubbleRef}
          className="ais-tooltip-text"
          role="tooltip"
          style={{
            position: 'fixed',
            top: box.top,
            left: box.left,
            transform: box.transform,
            padding: '5px 10px',
            borderRadius: 7,
            background: 'var(--t1)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default Tooltip;
